import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from './LeadSourceEdit.module.css';

const LeadSourceEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    name: ''
  });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [useMockData, setUseMockData] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadLeadSource();
  }, [id]);

  const loadLeadSource = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      setInitialLoading(true);
      const response = await fetch(`${baseUrl}/api/leadsources/${id}/edit`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data && data.data.leadSource) {
          setFormData({
            name: data.data.leadSource.name || ''
          });
          setUseMockData(false);
        } else {
          throw new Error('Invalid response format');
        }
      } else {
        throw new Error('Failed to load lead source');
      }
    } catch (error) {
      console.error('Error loading lead source:', error);
      setUseMockData(true);
      // Use mock data as fallback
      setFormData({
        name: getMockLeadSourceName(id)
      });
      toast.warning('Using mock data due to backend connection issue');
    } finally {
      setInitialLoading(false);
    }
  };

  const getMockLeadSourceName = (id) => {
    const mockData = [
      { id: 1, name: 'ACT-ON' },
      { id: 2, name: 'Advertising Boards' },
      { id: 3, name: 'Anthony Laville\'s Property Meet Up' },
      { id: 4, name: 'Assets & Estates Management Conference 2017' },
      { id: 5, name: 'Assets and Estate Management 2018' },
      { id: 6, name: 'B.E Norwich' },
      { id: 7, name: 'Built Environment Networking - Leicester' },
      { id: 8, name: 'Campaign Monitor' },
      { id: 9, name: 'Canvassing' },
      { id: 10, name: 'CIH Brighton 2025' },
      { id: 11, name: 'CIH Brighton Conference May 2022' },
      { id: 12, name: 'CIH Housing 2019' },
      { id: 13, name: 'CIH housing 22' },
      { id: 14, name: 'CIH Manchester 2025' },
      { id: 15, name: 'CoStar' },
      { id: 16, name: 'Costar email Oct 2019' },
      { id: 17, name: 'CURRENT CLIENT REFERRAL' },
      { id: 18, name: 'Dementia and Care Home Expo' },
      { id: 19, name: 'DEVELOP CROYDON' },
      { id: 20, name: 'DEVELOPMENT SUMMIT' },
      { id: 21, name: 'DOMINIC SLATTER' },
      { id: 22, name: 'EG AWARDS 2017' },
      { id: 23, name: 'ENHANCING CROYDON' },
      { id: 24, name: 'EREN ELMAZ' },
      { id: 25, name: 'ESTATE GAZETTE' },
      { id: 26, name: 'Future of Housing 2019' },
      { id: 27, name: 'Google' },
      { id: 28, name: 'LinkedIn' },
      { id: 29, name: 'Property Portal' },
      { id: 30, name: 'Referral' }
    ];
    const leadSource = mockData.find(ls => ls.id === parseInt(id));
    return leadSource ? leadSource.name : '';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length > 255) {
      newErrors.name = 'Name cannot exceed 255 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      if (!useMockData) {
        const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const response = await fetch(`${baseUrl}/api/leadsources/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(formData)
        });
        
        const data = await response.json();
        
        if (response.ok) {
          toast.success(data.message || 'Lead source updated successfully!');
          navigate('/lead-sources');
        } else {
          toast.error(data.message || 'Failed to update lead source');
        }
      } else {
        // Mock update
        toast.success('Lead source updated successfully! (Mock)');
        navigate('/lead-sources');
      }
    } catch (error) {
      console.error('Error updating lead source:', error);
      toast.error('Failed to update lead source');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/lead-sources');
  };

  if (initialLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingSpinner}>
          <div className={styles.spinner}></div>
          <p>Loading lead source...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <nav className={styles.breadcrumb}>
        <ol>
          <li>
            <a href="/dashboard">Dashboard</a>
          </li>
          <li>
            <a href="/clients">Clients</a>
          </li>
          <li>
            <a href="/lead-sources">Lead Sources</a>
          </li>
          <li className={styles.active}>Edit Lead Source</li>
        </ol>
      </nav>

      {/* Content Area with Background */}
      <div className={styles.contentArea}>
        {/* Page Title */}
        <div className={styles.pageTitle}>
          <h1>Edit Lead Source</h1>
        </div>

        {/* Form Panel */}
        <div className={styles.formPanel}>
          <div className={styles.panelBody}>
            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.formGroup}>
                <label htmlFor="name" className={styles.formLabel}>
                  Name <span className={styles.required}>*</span>
                </label>
                <div className={styles.inputWrapper}>
                  <input
                    type="text"
                    className={`${styles.formInput} ${errors.name ? styles.inputError : ''}`}
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter lead source name"
                    maxLength={255}
                  />
                  {errors.name && (
                    <div className={styles.errorMessage}>
                      {errors.name}
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.formActions}>
                <button
                  type="submit"
                  className={`${styles.button} ${styles.saveButton}`}
                  disabled={loading}
                >
                  <i className={styles.checkIcon}>✓</i>
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadSourceEdit; 